import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || `https://api.frinksyn.com/api`;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const apiService = {
  // User registration
  registerUser: async (userData) => {
    try {
      const response = await api.post('/register', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // User login
  loginUser: async (userData) => {
    try {
      const response = await api.post('/login', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Upload old photo(s)
  uploadOldPhoto: async (userId, photoFiles) => {
    try {
      const formData = new FormData();
      if (Array.isArray(photoFiles)) {
        photoFiles.forEach((file) => {
          formData.append('photos', file);
        });
      } else if (photoFiles instanceof FileList) {
        Array.from(photoFiles).forEach((file) => {
          formData.append('photos', file);
        });
      } else {
        formData.append('photo', photoFiles);
        formData.append('photos', photoFiles);
      }
      formData.append('user_id', userId);
      
      const response = await api.post('/upload/old-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Upload present photos
  uploadPresentPhotos: async (userId, photoFiles) => {
    try {
      const formData = new FormData();
      const filesArray = Array.isArray(photoFiles) 
        ? photoFiles 
        : (photoFiles instanceof FileList ? Array.from(photoFiles) : [photoFiles]);
        
      filesArray.forEach((file) => {
        formData.append('photos', file);
      });
      formData.append('user_id', userId);
      
      const response = await api.post('/upload/present-photos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Upload post-a-photos
  uploadPostAPhotos: async (userId, photoFiles) => {
    try {
      const formData = new FormData();
      const filesArray = Array.isArray(photoFiles) 
        ? photoFiles 
        : (photoFiles instanceof FileList ? Array.from(photoFiles) : [photoFiles]);
        
      filesArray.forEach((file) => {
        formData.append('photos', file);
      });
      formData.append('user_id', userId);

      const response = await api.post('/upload/post-a-photos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Upload post-b-photos
  uploadPostBPhotos: async (userId, photoFiles) => {
    try {
      const formData = new FormData();
      const filesArray = Array.isArray(photoFiles) 
        ? photoFiles 
        : (photoFiles instanceof FileList ? Array.from(photoFiles) : [photoFiles]);
        
      filesArray.forEach((file) => {
        formData.append('photos', file);
      });
      formData.append('user_id', userId);

      const response = await api.post('/upload/post-b-photos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get user status
  getUserStatus: async (userIdOrName) => {
    try {
      if (/^\d+$/.test(userIdOrName)) {
        const response = await api.get(`/user/${userIdOrName}/status`);
        return response.data;
      } else {
        const response = await api.get(`/user/by-name/${encodeURIComponent(userIdOrName)}/status`);
        return response.data;
      }
    } catch (error) {
      throw error;
    }
  },

  // Update user profile metadata
  updateUserProfile: async (userId, profileData) => {
    try {
      const response = await api.put(`/user/${userId}/profile`, profileData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get all users (Admin)
  getAllUsers: async () => {
    try {
      const response = await api.get('/admin/users');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Admin Login
  adminLogin: async (username, password) => {
    try {
      const response = await api.post('/admin/login', { username, password });
      sessionStorage.setItem('adminToken', response.data.token);
      sessionStorage.setItem('isMasterAdmin', response.data.is_master);
      sessionStorage.setItem('adminUsername', response.data.username || username);
      sessionStorage.setItem('adminRole', response.data.role || 'admin');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get all admins (Master only)
  getAdmins: async () => {
    try {
      const response = await api.get('/admin/manage/admins');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create admin (Master only)
  createAdmin: async (username, password, role = 'admin') => {
    try {
      const response = await api.post('/admin/manage/admins', { username, password, role });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete admin (Master only)
  deleteAdmin: async (adminId) => {
    try {
      const response = await api.delete(`/admin/manage/admins/${adminId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update admin role (Master only)
  updateAdminRole: async (adminId, role) => {
    try {
      const response = await api.put(`/admin/manage/admins/${adminId}/role`, { role });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete user (Master only)
  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/admin/manage/users/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Bulk delete users (Master only)
  bulkDeleteUsers: async (userIds) => {
    try {
      const response = await api.post('/admin/manage/users/bulk-delete', { user_ids: userIds });
      return response.data;
    } catch (error) {
      throw error;
    }
  },


  adminLogout: () => {
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('isMasterAdmin');
    sessionStorage.removeItem('adminUsername');
    sessionStorage.removeItem('adminRole');
  },

  // Delete / Remove photo
  deletePhoto: async (photoId) => {
    try {
      const response = await api.delete(`/photos/${photoId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Replace a rejected photo with a new file
  replacePhoto: async (photoId, newFile) => {
    try {
      const formData = new FormData();
      formData.append('photo', newFile);
      const response = await api.put(`/photos/${photoId}/replace`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Override photo pose
  overridePhotoPose: async (photoId, pose) => {
    try {
      const response = await api.post(`/photos/${photoId}/override-pose`, { pose });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Approve photos
  approvePhotos: async (userId) => {
    const response = await api.post(`/user/${userId}/approve`);
    return response.data;
  },

  // Force Approve User (Admin only)
  forceApproveUser: async (userId) => {
    const token = sessionStorage.getItem('adminToken');
    const response = await api.post(`/user/${userId}/force-approve`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Undo Approve User (Admin only)
  undoApproveUser: async (userId) => {
    const token = sessionStorage.getItem('adminToken');
    const response = await api.post(`/user/${userId}/undo-approve`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  exportUserData: async (userId) => {
    const token = sessionStorage.getItem('adminToken');
    const response = await api.get(`/admin/user/${userId}/export`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob', // Important for handling binary data (ZIP)
    });
    return response.data;
  },

  getUserExportMetadata: async (userId) => {
    const token = sessionStorage.getItem('adminToken');
    const response = await api.get(`/admin/user/${userId}/export-metadata`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateUserExportMetadata: async (userId, metadata) => {
    const token = sessionStorage.getItem('adminToken');
    const response = await api.put(`/admin/user/${userId}/export-metadata`, metadata, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  renamePhoto: async (photoId, customFilename) => {
    const token = sessionStorage.getItem('adminToken');
    const response = await api.post(`/admin/photos/${photoId}/rename`, { custom_filename: customFilename }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Download a single photo as a ZIP with EXIF info sidecar
  downloadPhotoWithExif: async (photoId) => {
    const token = sessionStorage.getItem('adminToken');
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await api.get(`/photos/${photoId}/download-with-exif`, {
      headers,
      responseType: 'blob',
    });
    return response.data;
  },

  // Helper to get image full URL
  getPhotoUrl: (photoPathOrId) => {
    if (!photoPathOrId) return '';

    // If an object was passed directly (e.g. photo object)
    if (typeof photoPathOrId === 'object' && photoPathOrId !== null) {
      photoPathOrId = photoPathOrId.preview_url || photoPathOrId.url || photoPathOrId.file_path || photoPathOrId.id;
      if (!photoPathOrId) return '';
    }

    const str = String(photoPathOrId).trim();
    if (!str) return '';

    // Direct web, blob or data URIs (e.g. local preview or full external URL)
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:') || str.startsWith('blob:')) {
      return str;
    }

    // Pure numeric ID (e.g. 5 or "5")
    if (/^\d+$/.test(str)) {
      const base = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
      return `${base}/photos/${str}/file`;
    }

    // Clean base origin without trailing /api
    const origin = API_BASE_URL.replace(/\/api\/?$/, '').replace(/\/+$/, '');

    // Normalize relative path ensuring it starts with /api
    let path = str;
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    if (!path.startsWith('/api/')) {
      path = `/api${path}`;
    }

    return `${origin}${path}`;
  },

  // Get Example Photos
  getExamplePhotos: async () => {
    try {
      const response = await api.get('/example-photos');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Upload Example Photo
  uploadExamplePhoto: async (file, category) => {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('category', category);
      const response = await api.post('/example-photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete Example Photo
  deleteExamplePhoto: async (photoId) => {
    try {
      const response = await api.delete(`/example-photos/${photoId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export default apiService;
